import { ProductName } from '@pypestream/api-services';

import { CommonProduct, GetProductsProps } from '../universal-nav/types';
import { generateBaseURLsToTools } from './generate-base-urls/generate-base-urls';

interface GetProducts {
  (props: GetProductsProps): CommonProduct[];
}

export const getProducts: GetProducts = ({
  project,
  allProducts,
  assignedRoles,
}) => {
  const { id: projectId } = project;

  const productIds = allProducts.reduce(
    (acc, { name, id }) => ({ ...acc, [`${name}`]: id }),
    {} as Record<ProductName, string>
  );

  const projectProducts =
    project?.projectProductSettings?.map(({ productId }) => productId) || [];

  const hasAgentAssistRoles = !!assignedRoles.find(
    (assignedRole) => assignedRole.product.name === ProductName.AgentAssist
  );

  const hasAnalyticsRoles = !!assignedRoles.find(
    (assignedRole) => assignedRole.product.name === ProductName.Analytics
  );
  const hasProStudioRoles = !!assignedRoles.find(
    (assignedRole) => assignedRole.product.name === ProductName.ProStudio
  );

  const productBaseURLs = generateBaseURLsToTools(window.location);
  const managerURL = window.location.host.includes('manager')
    ? `/organization/${project.accountId}/projects/${projectId}`
    : `${productBaseURLs.Manager}/organization/${project.accountId}/projects/${projectId}`;
  const contactCenterUrl =
    hasAgentAssistRoles && projectId
      ? `${productBaseURLs.ContactCenter}/app/accounts/${projectId}/dashboard`
      : undefined;
  const analyticsURL = `${productBaseURLs.Analytics}/api/projects/${projectId}/change`;
  const proStudioURL = `${productBaseURLs.ProStudio}/${projectId}`;

  const commonProductsProps: CommonProduct[] = [
    {
      name: ProductName.Organization,
      label: 'Manager',
      logo: 'manager',
      url: managerURL,
      disabled: false,
      // It means Manager is always available.
      integrated: true,
      index: 1,
    },
    {
      name: ProductName.AgentAssist,
      label: 'Contact Center',
      logo: 'contactCenter',
      url: contactCenterUrl,
      conditional: true,
      disabled: !hasAgentAssistRoles,
      index: 2,
    },
    {
      name: ProductName.Analytics,
      label: 'Analytics',
      logo: 'analytics',
      // @todo: generate correct link for Analytics when service will be ready
      url: analyticsURL,
      conditional: true,
      disabled: !hasAnalyticsRoles,
      index: 3,
    },
    {
      name: ProductName.ProStudio,
      label: 'Pro Studio',
      logo: 'proStudio',
      url: proStudioURL,
      conditional: true,
      disabled: !hasProStudioRoles,
      index: 4,
    },
  ];
  const products: CommonProduct[] = commonProductsProps
    .map((cProduct) => {
      const productId = cProduct.name ? productIds?.[cProduct.name] || '' : '';
      const integrated = projectId
        ? projectProducts?.includes(productId)
        : false;
      const isDisabled = cProduct.conditional
        ? cProduct.disabled || !integrated
        : cProduct.disabled;

      return {
        ...cProduct,
        id: productId,
        disabled: isDisabled && cProduct.name !== ProductName.Organization,
        integrated: cProduct.integrated || integrated,
      };
    })
    .filter((cProduct) => cProduct.integrated);

  return products;
};
